import { useState } from 'react';
import { DiscountProduct } from '@/types/discount';
import { ProductDiscountCard } from '../cards/ProductDiscountCard';

interface AvailableSectionProps {
  products: DiscountProduct[];
  type: 'deadline' | 'reservation';
  onDiscountRateChange: (productId: string, discountRate: number) => void;
  onStockChange: (productId: string, stock: number) => void;
  onPriceChange?: (productId: string, price: number) => void;
  onProductSelect: (productId: string) => void;
  onSelectAll?: (select: boolean) => void;
}

export function AvailableSection({
  products,
  type,
  onDiscountRateChange,
  onStockChange,
  onPriceChange,
  onProductSelect,
  onSelectAll
}: AvailableSectionProps) {
  const [searchTerm, setSearchTerm] = useState('');

  // 검색 필터만 적용 (이미 availableProducts로 필터링된 상품들)
  const filteredProducts = products
    .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));

  // 전체 선택 상태 계산
  const allSelected = filteredProducts.length > 0 && filteredProducts.every(p => p.selected);

  return (
    <div>
      {/* 검색 바 */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="등록할 제품을 검색하세요"
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <svg
            className="absolute right-3 top-2.5 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* 헤더 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-800">
            등록 가능한 상품
          </h3>
          {filteredProducts.length > 0 && onSelectAll && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => onSelectAll(e.target.checked)}
                className="w-4 h-4 text-green-500 rounded focus:ring-2 focus:ring-green-500"
              />
              <span className="text-sm text-gray-600">전체 선택</span>
            </label>
          )}
        </div>
        <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
          {filteredProducts.length}개 상품
        </span>
      </div>

      {/* 상품 목록 */}
      {filteredProducts.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-2.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 009.586 13H7" />
          </svg>
          <p className="text-gray-500 text-sm">
            {searchTerm 
              ? `'${searchTerm}'에 해당하는 상품이 없습니다`
              : '등록 가능한 상품이 없습니다'
            }
          </p>
          {searchTerm && (
            <p className="text-gray-400 text-xs mt-1">검색어를 다시 확인해주세요</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredProducts.map((product) => (
            <ProductDiscountCard
              key={product.productId}
              product={product}
              type={type}
              onDiscountRateChange={onDiscountRateChange}
              onStockChange={onStockChange}
              onPriceChange={onPriceChange}
              onProductSelect={onProductSelect}
              isRegistered={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}